// The "Ask" box: a question → /api/ask (Clef picks a plan, the API computes, templates write the answer) → the answer,
// how the question was read, and the view applied through the app's own URL state.
import { openSignup } from "./account.ts";
import { api, type AskResult, type Level, type Metric } from "./api.ts";
import { LEVEL_LABEL } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import type { Budget } from "./budget.ts";
import { inDeepState, type Scope } from "./state.ts";

const INTENT_LABEL: Record<string, string> = { rank: "Ranking", area: "About a place", history: "Change over time", forecast: "Outlook", compare: "Comparison", relate: "Relationship", outside: "Outside the data" };

/**
 * The scope an answer opens in: the visitor's own, unless its places need the other one. City is California's level,
 * and a place outside California can't be shown in the California view. A link without a scope opens in California
 * (web/state.ts linkScope), so the answer's links always carry one.
 */
export function answerScope(current: Scope, level: Level, places: string[]): Scope {
  if (level === "city") return "ca";
  if (places.some((id) => inDeepState(id) === false)) return "us";
  return current;
}

/** App URL for a view spec (the hash the app reads; see main.ts readHash), in `scope` when given. */
export function viewHash(v: NonNullable<AskResult["view"]>, scope?: Scope): string {
  const h = viewHashOf(v);
  return scope ? `${h}&scope=${scope}` : h;
}

function viewHashOf(v: NonNullable<AskResult["view"]>): string {
  const q = new URLSearchParams({ level: v.level, metric: v.metric });
  if (v.view === "relate") {
    if (v.y) q.set("y", v.y);
    if (v.geo) q.set("within", v.geo);
    return `#/relate?${q}`;
  }
  if (v.view === "compare") {
    if (v.ids?.length) q.set("ids", v.ids.join(","));
    return `#/compare?${q}`;
  }
  if (v.view === "screen") {
    // the Screener (web/screen.ts screenParams): the price limit, the area it's in, sorted by the answer's data point
    for (const f of v.filters ?? []) q.append("f", `${f.metric}:${f.op}:${f.value}`);
    if (v.geo) { q.set("in", v.geo); q.set("wn", v.withinName ?? v.geo); }
    q.set("sort", v.metric);
    q.set("dir", v.sort === "asc" ? "asc" : "desc");
    return `#/screen?${q}`;
  }
  if (v.geo) q.set("geo", v.geo);
  if (v.view === "table" && v.within) q.set("within", v.within);
  if (v.view === "table" && v.sort === "asc") q.set("sort", "asc");
  return `#${v.view === "table" ? "/table" : ""}?${q}`;
}

export interface AskDeps {
  metrics: Map<string, Metric>;
  context: () => { level: Level; metric: string; geo: string | null; scope: Scope };
  /** mark a ranking's places on the map (empty = clear) */
  showPlaces: (level: Level, items: NonNullable<AskResult["items"]>) => void;
  /** open one place (a click on its row) */
  pickPlace: (geoId: string) => void;
  /** emphasize one place on the map (a hover on its row), or none */
  focusPlace: (geoId: string | null) => void;
  /** a budget the question gave ("on $180k income with 20% down"): kept as My budget before the view opens */
  useBudget: (b: Budget) => void;
}

const usd = (v: number) => (v >= 1e6 ? `$${Number((v / 1e6).toFixed(2))}M` : `$${Math.round(v / 1e3)}K`);
/** The numbers read from the question, for the plan line: "$180K income · 20% down · under $1.2M". */
export function describeNumbers(n: NonNullable<AskResult["numbers"]>): string {
  return [
    n.income != null ? `${usd(n.income)} income` : "",
    n.down ? ("usd" in n.down ? `${usd(n.down.usd)} down` : `${Math.round(n.down.share * 1000) / 10}% down`) : "",
    n.rate != null ? `${n.rate}% rate` : "",
    n.maxPrice != null ? `under ${usd(n.maxPrice)}` : "",
    n.minPrice != null ? `over ${usd(n.minPrice)}` : "",
    n.years != null ? `${n.years} years` : "",
  ].filter(Boolean).join(" · ");
}

const QUESTION_START = /^(what|where|which|who|when|why|how|is|are|was|will|would|should|could|can|do|does|did|cheapest|priciest|best|worst|top|most|least|lowest|highest|compare|show|find|rank|list)\b/i;

/**
 * The sidebar's one box: Enter opens a place, picks a data point or asks a question. A data point whose name starts
 * with what was typed (3+ letters: "rent", "cap rate") wins unless the place is an exact hit (its code, or its name up
 * to the comma); "Renton" still opens Renton. Then a question mark, a question word up front, " vs ", or four words or
 * more read as a question; otherwise the top place match wins (no match: ask). `top` is the first search result's
 * name / code, `metric` the label of the first data point that matches. Without Ask it's a place or data point search.
 */
export function routeQuery(q: string, top: { name: string; code: string } | null, askOn = true, metric: string | null = null): "place" | "metric" | "ask" {
  const t = q.trim();
  if (metricFirst(t, top, metric)) return "metric";
  if (!askOn) return top || !metric ? "place" : "metric";
  if (top && (t === top.code || top.name.toLowerCase().startsWith(t.toLowerCase()))) return "place";
  if (/\?/.test(t) || QUESTION_START.test(t) || /\bvs\.?\b|\bversus\b/i.test(t) || t.split(/\s+/).length >= 4) return "ask";
  return top ? "place" : "ask";
}

/** Does the data point go above the places in the box's list (and win Enter)? See routeQuery. */
export function metricFirst(q: string, top: { name: string; code: string } | null, metric: string | null): boolean {
  const t = q.trim().toLowerCase();
  if (!metric || t.length < 3 || t.includes("?") || !metric.toLowerCase().startsWith(t)) return false;
  return !top || (t !== top.code.toLowerCase() && top.name.split(",")[0]!.trim().toLowerCase() !== t);
}

export function askBox(deps: AskDeps & { onClear: () => void }) {
  const out = el("div", { class: "ask-out", "aria-live": "polite" });
  let seq = 0;
  const label = (id: string) => deps.metrics.get(id)?.label ?? id;
  const submit = async (question: string) => {
    const q = question.trim();
    if (!q) return;
    const mine = ++seq;
    deps.showPlaces(deps.context().level, []);
    out.replaceChildren(el("div", { class: "muted small" }, "Thinking…"));
    const slow = setTimeout(() => { if (mine === seq) out.replaceChildren(el("div", { class: "muted small" }, "Still working: the AI service is slow right now…")); }, 3000);
    try {
      const { scope: current, ...context } = deps.context();
      const r = await api.ask(q, context).finally(() => clearTimeout(slow));
      if (mine !== seq) return;
      const pct = (p: number) => `${Math.round(p * 100)}%`;
      // a ranking: the sentence up to its list, then the places as numbered links that match the pins on the map
      const compare = r.view?.view === "compare";
      const items = r.view && r.items?.length && !compare ? r.items : null;
      const scope = r.view ? answerScope(current, r.view.level, [...(items ?? []).map((x) => x.geo_id), ...(r.view.geo ? [r.view.geo] : []), ...(r.view.ids ?? [])]) : current;
      const list = items ? `: ${items.map((i) => `${i.label} (${i.value})`).join(", ")}.` : "";
      const answer = items && r.answer.endsWith(list) ? `${r.answer.slice(0, -list.length)}:` : r.answer;
      out.replaceChildren(...nodes(
        el("p", { class: "ask-answer" }, answer),
        items ? el("ol", { class: "ask-items" }, ...items.map((i, n) => el("li", {},
          el("button", { class: "ask-item", "data-geo": i.geo_id, title: `Open ${i.label}`,
            onclick: () => deps.pickPlace(i.geo_id),
            onmouseenter: () => deps.focusPlace(i.geo_id), onmouseleave: () => deps.focusPlace(null),
            onfocus: () => deps.focusPlace(i.geo_id), onblur: () => deps.focusPlace(null) },
          el("span", { class: "ask-num" }, String(n + 1)), el("span", { class: "ask-place" }, i.label), el("span", { class: "ask-value" }, i.value))))) : null,
        items ? el("div", { class: "ask-links small" },
          el("a", { href: viewHash(r.view!, scope) }, "Open the full table"),
          el("button", { class: "linkish", onclick: () => { deps.showPlaces(r.view!.level, []); out.replaceChildren(); deps.onClear(); } }, "Clear")) : null,
        el("div", { class: "ask-plan muted small", title: `How the question was read (${r.model}), with the decision model's confidence in each part` },
          `${INTENT_LABEL[r.plan.intent] ?? r.plan.intent}${r.plan.metric ? ` · ${label(r.plan.metric)}` : ""}`,
          r.plan.intent === "rank" ? ` · ${LEVEL_LABEL[r.plan.level]} · ${r.plan.sort === "asc" ? "lowest first" : "highest first"}` : "",
          r.numbers ? ` · ${describeNumbers(r.numbers)}` : "",
          ` · confidence ${pct(r.plan.confidence)}`),
        r.alternatives.length ? el("div", { class: "ask-alts small" }, "Or did you mean: ",
          ...r.alternatives.map((id) => el("a", { href: r.view ? viewHash({ ...r.view, metric: id }, scope) : `#?metric=${id}&scope=${scope}`, class: "chip" }, label(id)))) : null,
      ));
      // the budget first: the map, table and panel read My Payment from it
      if (r.budget) deps.useBudget(r.budget);
      // a ranking stays on the map with its places marked (the table is a link); other answers (a comparison: the
      // Compare view) open their view
      if (items && r.view?.view !== "screen") {
        location.hash = viewHash({ ...r.view!, view: "map", geo: null }, scope);
        deps.showPlaces(r.view!.level, items);
      } else if (r.view) location.hash = viewHash(r.view, scope);
    } catch (e) {
      if (mine !== seq) return;
      out.replaceChildren(el("div", { class: "panel-error small" }, (e as Error).message));
      if ((e as { limit?: string }).limit === "signup") openSignup({ source: "ask", dismissible: true });
    }
  };
  return {
    el: el("div", { class: "ask" }, out),
    run: (q: string) => void submit(q),
    /** light up a place's row (a hover on its pin or area on the map) */
    highlight(geoId: string | null) {
      for (const b of out.querySelectorAll<HTMLElement>(".ask-item")) b.classList.toggle("focus", b.dataset.geo === geoId);
    },
  };
}
